/**
 * A dataset's settings: name, topic, description, language, and whether it is a base
 * dataset every instance searches (is_global). Changing is_global is confirmed with
 * what it affects. There is no delete (specs/dataset-workspace.md, #805).
 */
import React, { useState } from 'react';
import { Input, TextArea, Select, Label, Button, Actions, StatusLine, FieldHelp } from '../../../components/InstanceSettings/formPrimitives';
import { useTranslation } from '../../../hooks/useTranslation.jsx';
import { useConfirm } from '../../../components/DialogProvider';
import { updateDataset } from './datasetApi';
import DatasetAccess from './DatasetAccess';
import { FieldRow } from './styles';

export default function DatasetSettings({ dataset, onSaved, onCancel }) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [form, setForm] = useState({
    name: dataset.name || '',
    topic: dataset.topic || '',
    description: dataset.description || '',
    language: dataset.language || 'fi',
    is_global: !!dataset.is_global,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (key) => (e) => setForm({ ...form, [key]: key === 'is_global' ? e.target.checked : e.target.value });

  const confirmText = (to, impact) => {
    const others = (impact.other_globals || []).map((d) => d.name).join(', ');
    const lines = [
      t(to ? 'datasetWorkspace.settings.confirmGlobalOn' : 'datasetWorkspace.settings.confirmGlobalOff', { count: impact.instances }),
    ];
    if (to && others) lines.push(t('datasetWorkspace.settings.otherGlobals', { names: others }));
    return lines.join('\n\n');
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      let saved;
      try {
        saved = await updateDataset(dataset.id, form);
      } catch (err) {
        if (!err.body?.needsConfirm) throw err;
        if (!(await confirm({ message: confirmText(err.body.to, err.body.impact), destructive: true }))) return;
        saved = await updateDataset(dataset.id, { ...form, confirmGlobal: true });
      }
      onSaved(saved);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
    <form onSubmit={save} style={{ marginTop: 14 }}>
      <FieldRow>
        <div>
          <Label htmlFor="dss-name">{t('datasetWorkspace.nameLabel')}</Label>
          <Input id="dss-name" value={form.name} onChange={set('name')} required />
        </div>
        <div>
          <Label htmlFor="dss-topic">{t('datasetWorkspace.topicLabel')}</Label>
          <Input id="dss-topic" value={form.topic} onChange={set('topic')} />
        </div>
        <div>
          <Label htmlFor="dss-lang">{t('datasetWorkspace.languageLabel')}</Label>
          <Select id="dss-lang" value={form.language} onChange={set('language')}>
            <option value="fi">suomi</option>
            <option value="en">English</option>
            <option value="sv">svenska</option>
          </Select>
        </div>
      </FieldRow>
      <Label htmlFor="dss-desc">{t('datasetWorkspace.settings.description')}</Label>
      <TextArea id="dss-desc" value={form.description} onChange={set('description')} style={{ minHeight: 70 }} />
      <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12, color: '#ddd', fontSize: 14 }}>
        <input type="checkbox" checked={form.is_global} onChange={set('is_global')} />
        {t('datasetWorkspace.settings.global')}
      </label>
      <FieldHelp>{t('datasetWorkspace.settings.globalHelp')}</FieldHelp>
      <Actions>
        <Button type="submit" $size="small" $variant="primary" disabled={busy || !form.name.trim()}>{t('datasetWorkspace.save')}</Button>
        <Button type="button" $size="small" $variant="ghost" onClick={onCancel}>{t('datasetWorkspace.cancel')}</Button>
        {error && <StatusLine $error>{error}</StatusLine>}
      </Actions>
    </form>
    <DatasetAccess datasetId={dataset.id} />
    </>
  );
}
